import { Component, Input, OnInit } from '@angular/core';
import { AdminScopes, AdminUser, UserService } from '@app/shared/services/user.service';
import { FinanceService } from '@app/pages/authenticated/pages/finance/finance.service';
import {
  DateFormats,
  DefaultTimezone,
  PanelWidths,
  ShippingProviderNames,
  SupportedReconProvideItems,
  SupportedReconProvider,
} from '@app/shared/constants';
import { Sort, SortDirection } from '@angular/material/sort';
import { HelpersService } from '@app/shared/services/helpers.service';
import { DateTime } from 'luxon';
import { Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { environment } from '../../../../../../../environments/environment';
import { SessionStorageService } from 'ngx-webstorage';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { MtxDrawer } from '@ng-matero/extensions/drawer';
import { UploadCourierInvoiceComponent } from '../upload-courier-invoice/upload-courier-invoice.component';
import { ModalConfirmationComponent } from '../../../../../../shared/components/modal-confirmation/modal-confirmation.component';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import {
  getDuplicatesHintText,
  getFailedRowsBadgeText,
  getRetryButtonLabel,
  getRowsCellText,
  getRowsOutcomeLines,
  getRowsOutcomeTone,
  getStatusLabel,
  getStatusTooltipLines,
  isRetryVisible,
  isTriggerReconVisible,
  RowsOutcomeTone,
} from '../courier-invoice-outcome.rules';

// Rows info icon colour per outcome tone (classes generated from $colors in _common.scss).
const ROWS_OUTCOME_TONE_CLASS: Record<RowsOutcomeTone, string> = {
  danger: 'text--danger',
  warning: 'text--warn',
  success: 'text--success-green',
};

// Status filter options for the Courier Invoices status mat-select. Mirrors ReconAuditStatus's
// string values from app-services (recon-audit.entity.ts) -- no cross-app import available, so
// kept in sync manually here, same convention as UPLOAD_SOURCE_PROVIDERS above.
const STATUS_FILTER_ITEMS: { id: string; value: string; text: string }[] = [
  { id: 'all', value: 'all', text: 'All' },
  { id: 'pending', value: 'pending', text: 'Pending' },
  { id: 'processing', value: 'processing', text: 'Processing' },
  { id: 'processed', value: 'processed', text: 'Processed' },
  { id: 'processed_with_failures', value: 'processed_with_failures', text: 'Processed with failures' },
];

// The query params sent to admin/finance/invoices. invoiceNumber/startDate/endDate/status are
// conditionally deleted below (mutually-exclusive invoice-number vs. date-range search, and
// 'all' status is never sent). `provider` is never deleted -- its 'all' value IS sent and the
// backend handles it. Each field stays optional rather than a plain `any`.
interface CourierInvoiceSearchParams {
  startDate?: string | null;
  endDate?: string | null;
  invoiceNumber?: string;
  provider?: string;
  status?: string;
  orderByValue: string;
  orderByDirection: string;
  page: number;
  itemsPerPage: number;
}

@Component({
  standalone: false,
  selector: 'app-courier-invoices',
  templateUrl: './courier-invoices.component.html',
})
export class CourierInvoicesComponent implements OnInit {
  @Input() user: AdminUser;

  public canUpload: boolean = false;
  public loading: boolean = true;
  public CDN_URL: string = environment.CDN_URL;
  public searchForm: FormGroup | undefined;
  public displayedColumns: string[] = [
    'courier',
    'invoice_number',
    'invoice_amount',
    'file_name',
    'rows',
    'status',
    'updated_at',
  ];
  public DATE_FORMAT: string = DateFormats.DATE_SHORT;
  public courierInvoices: any = [];
  public courierInvoicesCount: number;
  public defaultTimezone = DefaultTimezone;
  public providerItems = SupportedReconProvideItems;
  public statusItems = STATUS_FILTER_ITEMS;
  public dateRange = {
    start: DateTime.now().setZone(this.defaultTimezone).minus({ month: 1 }).toJSDate(),
    end: DateTime.now()
      .setZone(this.defaultTimezone)
      .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
      .toJSDate(),
  };
  public range = new FormGroup({
    start: new FormControl<Date | null>(this.dateRange.start, [Validators.required]),
    end: new FormControl<Date | null>(this.dateRange.end, [Validators.required]),
  });

  private currentSearchParams: any;
  public currentPage: number = 0;
  public itemsPerPage: number = 10;
  public itemsPerPageOptions: number[] = [10, 20, 50];
  public orderByValue: string = 'updated_at';
  public orderByDirection: SortDirection = 'desc';

  constructor(
    private auditService: FinanceService,
    private helpersService: HelpersService,
    private formBuilder: FormBuilder,
    private router: Router,
    private bannerNotificationsService: BannerNotificationsService,
    private sessionStorageService: SessionStorageService,
    private userService: UserService,
    private dialog: MatDialog,
    private drawer: MtxDrawer
  ) {}

  ngOnInit() {
    if (this.user) {
      this.canUpload = this.userService.hasAuthScope(this.user, [AdminScopes.MODIFY_FINANCE]);
      this.providerItems.sort((a, b) => a.text.localeCompare(b.text));
      this.providerItems.unshift({
        id: <SupportedReconProvider>'all',
        value: <SupportedReconProvider>'all',
        text: <ShippingProviderNames>'All',
      });
      this.createForm();
      this.reset();
    }
  }

  ngOnDestroy() {
    this.providerItems.shift();
  }

  public reset() {
    this.loading = true;
    this.courierInvoices = [];
    this.courierInvoicesCount = 0;
    this.currentPage = 0;
    this.range.patchValue({
      start: this.dateRange.start,
      end: this.dateRange.end,
    });
    this.createForm();
    this.getCourierInvoicesAudit();
  }

  public search() {
    this.getCourierInvoicesAudit();
  }

  private createForm() {
    this.searchForm = this.formBuilder.group({
      provider: ['all'],
      status: ['all'],
      invoiceNumber: [],
      startDate: [this.dateRange.start, [Validators.required]],
      endDate: [this.dateRange.end, [Validators.required]],
    });
  }

  public sortData(sort: Sort) {
    this.orderByValue = sort.active;
    this.orderByDirection = sort.direction;
    if (sort.direction === '') {
      this.orderByValue = 'updated_at';
      this.orderByDirection = 'desc';
    }

    this.getCourierInvoicesAudit();
  }

  public uploadInvoice() {
    const drawerRef = this.drawer.open(UploadCourierInvoiceComponent, {
      width: PanelWidths.desktopFull,
      disableClose: true,
      closeOnNavigation: false,
      data: { adminUser: this.user },
    });
    drawerRef.afterDismissed().subscribe((result) => {
      if (result) {
        this.getCourierInvoicesAudit();
        // Aligned to the drawer's accepted-not-done vocabulary: the upload was accepted and
        // enqueued, not fully reconciled — "saved" implied completion, which isn't true yet.
        this.bannerNotificationsService.success('Invoice accepted. Reconciliation is running in the background.');
      }
    });
  }

  // FILE column text: the name finance picked when uploading (`original_file_name`), never the
  // generated stored name once an original name is on record. Legacy rows uploaded before this
  // field existed have no `original_file_name` and fall back to the stored `file_name`.
  public getDisplayFileName(element: any): string {
    return element.original_file_name || element.file_name;
  }

  public openInvoiceDetail(invoice): void {
    this.sessionStorageService.store('selectedFinanceTab', 'app-courier-invoices');
    this.sessionStorageService.store('financeTabCourierSearch', this.currentSearchParams);
    this.router.navigate(['finance', 'invoice-audits', invoice.id]);
  }

  // Outcome and Retry rules live in courier-invoice-outcome.rules (shared with the detail page).
  public isTriggerReconVisible(element: any): boolean {
    return isTriggerReconVisible(element);
  }

  public getStatusLabel(element: any): string {
    return getStatusLabel(element);
  }

  public getRowsCellText(element: any): string {
    return getRowsCellText(element);
  }

  public getRowsOutcomeTooltipText(element: any): string {
    return getRowsOutcomeLines(element).join('\n');
  }

  public getRowsOutcomeIconClass(element: any): string {
    const tone = getRowsOutcomeTone(element);
    return tone ? ROWS_OUTCOME_TONE_CLASS[tone] : '';
  }

  public getStatusTooltipText(element: any): string {
    return getStatusTooltipLines(element).join('\n');
  }

  public getFailedRowsBadgeText(element: any): string {
    return getFailedRowsBadgeText(element);
  }

  public isRetryVisible(element: any): boolean {
    return isRetryVisible(element);
  }

  public getRetryButtonLabel(element: any): string {
    return getRetryButtonLabel(element);
  }

  public getDuplicatesHintText(element: any): string {
    return getDuplicatesHintText(element);
  }

  // After a Retry started from the status cell, reload so the row shows its new status.
  public onRetried(): void {
    this.getCourierInvoicesAudit();
  }

  public runReconciliation(invoice): void {
    this.showConfirmationDialog(invoice, () => {
      this.auditService.triggerRecon(invoice.courier, invoice.file_name).subscribe((res) => {
        this.bannerNotificationsService.success('Reconciliation triggered');
        this.getCourierInvoicesAudit();
      });
    });
  }

  private showConfirmationDialog(invoice, callback: () => void): void {
    const dialogRef = this.dialog.open(ModalConfirmationComponent, {
      width: '600px',
      data: {
        title: `Run reconciliation`,
        content: `Are you sure you want to run reconciliation for ${invoice.providerName}?  Please allow 5 minutes before running reconciliation for another courier.`,
        confirmBtnLabel: 'Run reconciliation',
        btnColor: 'primary',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        callback();
      }
    });
  }

  public pageChanged(pageEvent: PageEvent): void {
    this.currentPage = pageEvent.pageIndex;
    this.itemsPerPage = pageEvent.pageSize;
    this.getCourierInvoicesAudit();
  }

  private getCourierInvoicesAudit() {
    this.loading = true;
    this.courierInvoices = [];
    const storedSearchParams = this.sessionStorageService.retrieve('financeTabCourierSearch');
    let invoiceNumber: string = '';
    let provider: string;
    let status: string;

    if (storedSearchParams) {
      this.sessionStorageService.clear('financeTabCourierSearch');

      invoiceNumber = storedSearchParams.invoiceNumber?.trim();
      provider = (storedSearchParams.provider || 'all').trim();
      status = (storedSearchParams.status || 'all').trim();
      if (
        (!invoiceNumber || invoiceNumber.length === 0) &&
        storedSearchParams.startDate &&
        storedSearchParams.endDate
      ) {
        this.range.patchValue({
          start: storedSearchParams.startDate,
          end: storedSearchParams.endDate,
        });
      }
      this.searchForm.patchValue({
        provider,
        status,
        invoiceNumber,
      });
      this.searchForm.patchValue({
        provider: provider,
        status: status,
        invoiceNumber: invoiceNumber,
      });
    } else {
      const searchValues = this.searchForm?.value;
      invoiceNumber = searchValues?.invoiceNumber || '';
      provider = searchValues?.provider;
      status = searchValues?.status;
    }

    const startDate = DateTime.fromJSDate(this.range.value.start)
      .setZone(this.defaultTimezone)
      .startOf('day')
      .toISO({ includeOffset: false });
    const endDate = DateTime.fromJSDate(this.range.value.end)
      .setZone(this.defaultTimezone)
      .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
      .toISO({ includeOffset: false });

    const searchParams: CourierInvoiceSearchParams = {
      startDate,
      endDate,
      invoiceNumber,
      provider,
      status,
      orderByValue: this.orderByValue,
      orderByDirection: this.orderByDirection.toUpperCase(),
      page: this.currentPage + 1,
      itemsPerPage: this.itemsPerPage,
    };
    if (!invoiceNumber || invoiceNumber.length === 0) {
      delete searchParams.invoiceNumber;
    } else {
      delete searchParams.startDate;
      delete searchParams.endDate;
    }
    // Unlike `provider`, the backend's status query is parsed by ParseEnumPipe and has no
    // 'all' fallback of its own -- 'all' must never be sent.
    if (!status || status === 'all') {
      delete searchParams.status;
    }

    this.currentSearchParams = {
      invoiceNumber,
      provider,
      status,
      startDate: this.range.value.start,
      endDate: this.range.value.end,
      page: this.currentPage + 1,
      itemsPerPage: this.itemsPerPage,
      orderByValue: this.orderByValue,
      orderByDirection: this.orderByDirection,
    };

    this.auditService.getInvoices(searchParams).subscribe(
      (result: { invoices: any[]; count: number }) => {
        result.invoices.forEach((invoice) => {
          invoice.providerName = this.helpersService.convertSupportedReconProvidersEnumToString(invoice.courier);
        });
        this.courierInvoices = result.invoices;
        this.courierInvoicesCount = result.count;
        this.loading = false;
      },
      (err: Error) => {
        this.bannerNotificationsService.error(err.message, {
          category: 'GET courier invoices data error',
          message: err,
        });
        this.loading = false;
      }
    );
  }
}
